import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import Input from '../Input';
import Button from '../Button';
import Dropdown from '../Dropdown';
import Toast from '../Toast';
import PlanService from '../../services/plan.service';
import SubscriptionService from '../../services/subscription.service';

interface PlanOption {
  id: number;
  plan_code: string;
  plan_name: string;
  product_id: number;
}

interface PlanChangeModalProps {
  open: boolean;
  subscriptionId: number;
  productId: number;
  currentPlanId: number;
  effectiveDate: string;
  onClose: () => void;
  onScheduled: () => void;
}

/** US-042 AC-2: New Plan excludes the current plan; Effective Date is always the subscription's End Date, read-only. */
const PlanChangeModal: React.FC<PlanChangeModalProps> = ({
  open,
  subscriptionId,
  productId,
  currentPlanId,
  effectiveDate,
  onClose,
  onScheduled,
}) => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [newPlanId, setNewPlanId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    PlanService.list({ includeDeleted: false, limit: 100, productId }).then((response: any) => {
      setPlans(response?.data?.items ?? []);
    });
    setNewPlanId('');
    setReason('');
    setError('');
  }, [open, productId]);

  const eligiblePlans = useMemo(() => plans.filter((plan) => plan.id !== currentPlanId), [plans, currentPlanId]);

  const handleConfirm = useCallback(async () => {
    if (!newPlanId) {
      setError(t('subscriptions.planChangeModal.pageTexts.fields.newPlan.errors.required'));
      return;
    }
    setSubmitting(true);
    try {
      await SubscriptionService.schedulePlanChange(subscriptionId, { newPlanId: Number(newPlanId), reason: reason || undefined });
      toast.success(<Toast message={t('subscriptions.planChangeModal.pageTexts.success')} />, { icon: false });
      onScheduled();
    } finally {
      setSubmitting(false);
    }
  }, [newPlanId, reason, subscriptionId, onScheduled, t]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('subscriptions.planChangeModal.pageTexts.title')}
      content={
        <div className="row g-3">
          <div className="col-12">
            <Dropdown
              id="new_plan_id"
              name="new_plan_id"
              label={t('subscriptions.planChangeModal.pageTexts.fields.newPlan.label')}
              placeholder={t('subscriptions.planChangeModal.pageTexts.fields.newPlan.placeholder')}
              value={newPlanId}
              error={error}
              options={eligiblePlans.map((plan) => ({
                id: String(plan.id),
                name: `${plan.plan_code} — ${plan.plan_name}`,
              }))}
              noSearch
              onChange={(e) => setNewPlanId(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
          <div className="col-12">
            <Input
              id="plan_change_effective_date"
              name="plan_change_effective_date"
              label={t('subscriptions.planChangeModal.pageTexts.fields.effectiveDate.label')}
              value={effectiveDate}
              disabled
            />
          </div>
          <div className="col-12">
            <Input
              id="plan_change_reason"
              name="plan_change_reason"
              label={t('subscriptions.planChangeModal.pageTexts.fields.reason.label')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
      }
      action={
        <>
          <Button className="btn btn-outline-secondary" text={t('subscriptions.form.pageTexts.buttons.cancel')} onClick={onClose} />
          <Button
            className="btn btn-primary"
            text={t('subscriptions.planChangeModal.pageTexts.confirm')}
            loading={submitting}
            onClick={handleConfirm}
          />
        </>
      }
    />
  );
};

export default PlanChangeModal;
